import {B2BOrderItem, ItemSubstitutionChoice} from '@buncha/home/types';
import {Box, HBox} from '@buncha/components/core/view';
import {CustomText, H6Text, PSmallText} from '@buncha/components/core/text';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {formatCurrency, getDefaultImageUrl} from '@buncha/utils/common';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import {Analytics} from '@buncha/config/analytics/analytics';
import {B2BUserScreens} from '@buncha/B2B/navigation/types';
import BackScreenHeader from '@buncha/B2B/components/backScreenHeader/BackScreenHeader';
import {CustomFlatList} from '@buncha/components/core/list';
import {CustomImage} from '@buncha/components/core/Image';
import {Divider} from 'native-base';
import {EventType} from '@buncha/config/analytics/type';
import Header from '@buncha/components/composites/Header/Header';
import If from '@buncha/components/conditional/If';
import {ItemTabNode} from '@buncha/components/shared/TabNode/ItemTabNode';
import {Modules} from '@buncha/utils/types';
import NoDataFound from '@buncha/B2B/components/noDataFound/NoDataFound';
import NoItemsUI from './NoItemsUI';
import {OrderItemStatus} from '@buncha/batchDetails/types';
import Tab from '@buncha/components/composites/Tabs/Tab';
import {styles} from './styles';
import {useFetchGetData} from '@buncha/config/hooks/axios';
import {useGetLocale} from '@buncha/localization/hooks';
import {useItemsGrouping} from '@buncha/B2B/hooks/OrderItemInfo';
import {useRoute} from '@react-navigation/native';
import {useSocket} from '@buncha/config/socket/hooks';

const OrderItemInfo: React.FC = () => {
  const theme = useColorThemeType();
  const [currentTab, setCurrentTab] = useState<string>(OrderItemStatus.picked);
  const muliFont = useFontTheme();
  const route = useRoute();
  const params:
    | {orderId?: string; runId?: number; legacyId?: number}
    | undefined = route?.params;

  const [getOrderItem, orderItemData, orderItemLoading, error] =
    useFetchGetData<{
      data: {data: B2BOrderItem[]};
    }>();
  const [getOrderItemForSocket, orderItemSocketData, , errorInSocket] =
    useFetchGetData<{
      data: {data: B2BOrderItem[]};
    }>();
  const {title, body} =
    useGetLocale(Modules.B2BUserOrder, B2BUserScreens.OrderItemInfo) ?? {};

  const orderItems = useMemo(
    () =>
      orderItemSocketData?.data?.data.length
        ? orderItemSocketData?.data?.data
        : orderItemData?.data.data || [],
    [orderItemData?.data.data, orderItemSocketData?.data?.data],
  );

  const groupedItems = useItemsGrouping(orderItems);

  const condition = Boolean(orderItems?.length);
  const socket = useSocket(condition);
  const runId = params?.runId;
  const orderLegacyId = params?.legacyId;

  const onTabChange = useCallback(
    (tabKey: string) => {
      setCurrentTab(tabKey);
      Analytics.eventWithProps('View Items Tab', {
        type: EventType.cta,
        tab: tabKey,
        itemsCount: groupedItems[tabKey as OrderItemStatus]?.length || 0,
      });
    },
    [groupedItems],
  );

  const divider = useCallback(
    () => (
      <Box style={styles.separatorWrapper}>
        <Divider
          style={[
            styles.separator,
            {
              backgroundColor: theme?.background.gray[200],
            },
          ]}
        />
      </Box>
    ),
    [theme?.background.gray],
  );

  const tabData = useMemo(() => {
    return [
      {
        key: OrderItemStatus.picked,
        data: body?.pickedItems?.replace(
          '{count}',
          groupedItems[OrderItemStatus.picked]?.length || 0,
        ),
      },
      {
        key: OrderItemStatus.missing,
        data: body?.substitutedItems?.replace(
          '{count}',
          groupedItems[OrderItemStatus.missing]?.length || 0,
        ),
      },
      {
        key: OrderItemStatus.dropped,
        data: body?.droppedItems?.replace(
          '{count}',
          groupedItems[OrderItemStatus.dropped]?.length || 0,
        ),
      },
      {
        key: OrderItemStatus.notPicked,
        data: body?.notPickedItems?.replace(
          '{count}',
          groupedItems[OrderItemStatus.notPicked]?.length || 0,
        ),
      },
    ];
  }, [
    body?.droppedItems,
    body?.notPickedItems,
    body?.pickedItems,
    body?.substitutedItems,
    groupedItems,
  ]);

  useEffect(() => {
    if (params?.orderId?.length)
      getOrderItem(`/api/v1/customer/order/${params?.orderId}/order-items`, {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.orderId]);

  useEffect(() => {
    if (runId && orderLegacyId)
      socket.on(
        `order_view_item_progress_${runId}_${orderLegacyId}`,
        async () =>
          getOrderItemForSocket(
            `/api/v1/customer/order/${params?.orderId}/order-items`,
            {},
          ),
      );

    return () => {
      socket.off(
        `order_view_item_progress_${runId}_${orderLegacyId}`,
        async () =>
          getOrderItemForSocket(
            `/api/v1/customer/order/${params?.orderId}/order-items`,
            {},
          ),
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderLegacyId, params?.orderId, runId, socket]);

  useEffect(() => {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) return;
      getOrderItemForSocket(
        `/api/v1/customer/order/${params?.orderId}/order-items`,
        {},
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    onTabChange(currentTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderItem = useCallback(
    (propes: {item: B2BOrderItem}) => {
      const {item} = propes;
      const isShopperSub =
        item.itemSubstitutionPreference === ItemSubstitutionChoice.shopperSub;
      const isCustomerSub =
        (item.itemSubstitutionPreference ===
          ItemSubstitutionChoice.customerSub ||
          item.itemSubstitutionPreference ===
            ItemSubstitutionChoice.strictCustomerNoSub) &&
        item.substituteOptions?.[0];
      return (
        <Box style={styles.itemContainer}>
          <HBox style={styles.itemRow}>
            <CustomImage
              style={styles.itemImage}
              source={item.imageUrl ?? ''}
              onErrorSource={getDefaultImageUrl()}
            />
            {/* for now disabled */}
            <If condition={false}>
              <Box
                style={[
                  styles.backupTag,
                  {borderColor: theme?.background.orange[100]},
                ]}>
                <PSmallText
                  style={{
                    fontFamily: muliFont[700].normal,
                    color: theme?.text.blue[300],
                  }}>
                  {body?.backup}
                </PSmallText>
              </Box>
            </If>
            <Box style={styles.itemDetailsContainer}>
              <H6Text
                numberOfLines={2}
                style={[
                  styles.itemTitle,
                  {
                    color: theme?.text.blue[300],
                    fontFamily: muliFont[400].normal,
                  },
                ]}>
                {item.quantity} x {item.name}
              </H6Text>
              <HBox>
                <CustomText style={styles.currencyText}>
                  {formatCurrency(item.price)}
                </CustomText>
              </HBox>
              <If
                condition={
                  item.status === OrderItemStatus.dropped &&
                  item.dropReason?.length
                }>
                <CustomText
                  numberOfLines={1}
                  style={[
                    styles.dropReasonText,
                    {
                      color: theme?.text.blue[300],
                    },
                  ]}>
                  <CustomText
                    style={{
                      fontFamily: muliFont[600].normal,
                    }}>
                    {body?.droppedReason}
                  </CustomText>
                  {item.dropReason}
                </CustomText>
              </If>
              <If
                condition={
                  item.status === OrderItemStatus.dropped &&
                  (isShopperSub || isCustomerSub) &&
                  !item.dropReason?.length
                }>
                <Box
                  style={[
                    styles.subsContainer,
                    {
                      borderColor: theme?.text.blue[100],
                    },
                  ]}>
                  <If condition={isShopperSub}>
                    <PSmallText
                      style={{
                        color: theme?.text.blue[300],
                      }}>
                      {body?.noShopperBackup}
                    </PSmallText>
                  </If>
                  <If condition={isCustomerSub}>
                    <HBox style={styles.customerSubContainer}>
                      <CustomImage
                        source={item.substituteOptions?.[0]?.imageUrl}
                        style={styles.subItemImage}
                        onErrorSource={getDefaultImageUrl()}
                      />
                      <Box style={styles.subNameContainer}>
                        <CustomText
                          numberOfLines={2}
                          alignText="left"
                          style={{
                            color: theme?.text.blue[300],
                          }}>
                          {item.substituteOptions?.[0]?.quantity} x{' '}
                          {item.substituteOptions?.[0]?.name}
                        </CustomText>
                        <PSmallText
                          style={{
                            color: theme?.text.blue[300],
                          }}>
                          {body?.noCustomerBackup}
                        </PSmallText>
                      </Box>
                    </HBox>
                  </If>
                </Box>
              </If>
            </Box>
          </HBox>

          {/* for now disabled */}
          <If condition={false}>
            <HBox
              style={[
                styles.replacementContainer,
                {backgroundColor: theme?.background.gray[200]},
              ]}>
              <CustomImage
                style={styles.replacementImage}
                source={item?.substitutedBy?.imageUrl ?? ''}
              />
              <Box style={styles.replacementTextBox}>
                <PSmallText
                  style={[
                    styles.replacementLabel,
                    {
                      color: theme?.text.blue[300],
                      fontFamily: muliFont[400].normal,
                    },
                  ]}>
                  {body?.originalItem}
                </PSmallText>
                <CustomText
                  numberOfLines={1}
                  style={[
                    styles.replacementTitle,
                    {
                      fontFamily: muliFont[600].normal,
                      color: theme?.text.blue[300],
                    },
                  ]}>
                  {item?.substitutedBy?.name}
                </CustomText>
              </Box>
            </HBox>
          </If>
        </Box>
      );
    },
    [
      body?.backup,
      body?.droppedReason,
      body?.noCustomerBackup,
      body?.noShopperBackup,
      body?.originalItem,
      muliFont,
      theme?.background.gray,
      theme?.background.orange,
      theme?.text.blue,
    ],
  );

  const sections: {[key: string]: React.ReactElement} = useMemo(() => {
    const data: {[key: string]: React.ReactElement} = {};
    const tabHeader = [
      OrderItemStatus.picked,
      OrderItemStatus.missing,
      OrderItemStatus.dropped,
      OrderItemStatus.notPicked,
    ];

    tabHeader.forEach(tab => {
      const items = groupedItems[tab];
      if (groupedItems[tab]?.length)
        data[tab] = (
          <CustomFlatList
            data={items}
            renderItem={renderItem}
            style={styles.listContainer}
            ItemSeparatorComponent={divider}
            contentContainerStyle={styles.listContentContainer}
            loading={orderItemLoading}
            showsVerticalScrollIndicator={false}
            loadMore={false}
            resetOnChangeKey={currentTab}
          />
        );
      else data[tab] = <NoItemsUI status={tab} />;
    });

    return data;
  }, [currentTab, divider, groupedItems, orderItemLoading, renderItem]);

  const tabNode = useCallback((info: any, activeKey: any) => {
    return <ItemTabNode info={info} activeKey={activeKey} />;
  }, []);

  // if no data
  if (error?.message || errorInSocket?.message) return <NoDataFound />;

  return (
    <Header
      showDefaultLeftHeader={false}
      hideBottomSafeArea
      hideUploadFile
      loading={orderItemLoading}>
      <BackScreenHeader title={title?.yourItems} />

      <Box style={styles.tabContainer}>
        <Tab
          activeKey={currentTab}
          tabData={tabData}
          sections={sections}
          style={styles.tab}
          tabNode={tabNode}
          setActiveKey={onTabChange}
        />
      </Box>
    </Header>
  );
};

export default React.memo(OrderItemInfo);
